import type { ICompanySegment } from "@repo/baseCloud";

import { useCompanySegmentFilterCascaded } from "@repo/baseCloud";
import { useCallback, useEffect, useRef, useState } from "react";

import { CompaniesDropdown, DivisionsDropdown, MarketsDropdown, RegionsDropdown } from ".";
import { ManagementAreaDropdown } from "./managementAreaDropdown";

export function CompanySegmentFilters(props: Props) {
	const { onChange, onUnfocused } = props;
	const [segmentFilters, setSegmentFilters] = useState<ICompanySegment>(emptyCompanySegmentFilters);
	const availableSegments = useCompanySegmentFilterCascaded(segmentFilters);

	const segmentChanged = useCallback((value: any, name: string & keyof ICompanySegment) => {
		setSegmentFilters((prev) => ({ ...prev, [name]: value }));
	}, []);

	useEffect(() => {
		if (props.value) {
			setSegmentFilters(props.value);
		}
	}, [props.value]);

	const prevFiltersRef = useRef<string>(emptyCompanySegmentFilterStr);

	useEffect(() => {
		const currentFiltersStr = JSON.stringify(segmentFilters);
		if (currentFiltersStr !== JSON.stringify(props.value) && currentFiltersStr !== prevFiltersRef.current) {
			prevFiltersRef.current = currentFiltersStr;
			onChange(segmentFilters);
		}
	}, [segmentFilters, onChange, props.value]);

	return (
		<>
			{props.includeCompany && (
				<CompaniesDropdown
					options={availableSegments.companies}
					loaded={availableSegments.companiesLoaded}
					value={segmentFilters.companyIds ?? []}
					changeHandler={segmentChanged}
					onUnfocused={onUnfocused}
					maxSelectedDisplay={3}
				/>
			)}
			<DivisionsDropdown
				options={availableSegments.divisions}
				loaded={availableSegments.divisionsLoaded}
				value={segmentFilters.companyDivisionIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<RegionsDropdown
				options={availableSegments.regions}
				loaded={availableSegments.regionsLoaded}
				value={segmentFilters.companyRegionIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<MarketsDropdown
				options={availableSegments.markets}
				loaded={availableSegments.marketsLoaded}
				value={segmentFilters.companyMarketIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<ManagementAreaDropdown
				options={availableSegments.managementAreas}
				loaded={availableSegments.managementAreasLoaded}
				value={segmentFilters.companyManagementAreaIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
		</>
	);
}

export const emptyCompanySegmentFilters: ICompanySegment = {
	companyIds: [],
	companyDivisionIds: [],
	companyRegionIds: [],
	companyMarketIds: [],
	companyManagementAreaIds: [],
};

const emptyCompanySegmentFilterStr = JSON.stringify(emptyCompanySegmentFilters);

interface Props {
	onChange: (filters: ICompanySegment) => void;
	value?: ICompanySegment;
	onUnfocused?: (event?: React.FocusEvent<any>) => void;
	includeCompany?: boolean;
}
